import receiptLogo from "@/assets/logo.jpeg";

export function formatDocumentLabel(documentType: string, folio: string | null, fallbackId: string): string {
  const type = {
    "33": "Factura afecta",
    "34": "Factura exenta",
    "39": "Boleta afecta",
    "41": "Boleta exenta",
  }[documentType] ?? `Documento ${documentType}`;
  const normalizedFolio = folio?.trim() ?? "";
  return normalizedFolio && /^\d+$/.test(normalizedFolio) ? `${type} #${normalizedFolio}` : `${type} sin folio`;
}

export function buildReceiptHtml(opts: {
  receiptNumber: string;
  paidAt: string;
  reference: string | null;
  customerName: string;
  customerRut: string;
  contractNumber: string;
  rows: Array<{ label: string; dueDate: string; amount: number }>;
  total: number;
}): string {
  const logo = receiptLogo;
  return `<html>
    <head>
      <title>Comprobante ${opts.receiptNumber}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        html, body { width: 72mm; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; line-height: 1.35; }
        .ticket { width: 72mm; }
        .logo-wrap { text-align: center; margin-bottom: 8px; }
        .logo { max-width: 38mm; max-height: 18mm; object-fit: contain; }
        .title { text-align: center; font-size: 16px; font-weight: 700; margin: 0; }
        .muted { color: #4b5563; font-size: 10px; text-align: center; }
        .section { margin-top: 10px; }
        .kv { margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 6px; }
        th, td { border-bottom: 1px dashed #d1d5db; padding: 5px 0; font-size: 10px; vertical-align: top; }
        th { text-align: left; }
        .right { text-align: right; }
        .total { font-size: 13px; font-weight: 700; text-align: right; margin-top: 10px; }
        .footer { margin-top: 10px; text-align: center; font-size: 10px; color: #4b5563; }
      </style>
    </head>
    <body>
      <div class="ticket">
        <div class="logo-wrap"><img class="logo" src="${logo}" alt="APR" /></div>
        <h1 class="title">Comprobante de pago</h1>
        <div class="muted">N.° ${opts.receiptNumber}</div>
        <div class="section">
          <div class="kv"><strong>Cliente:</strong> ${opts.customerName}</div>
          <div class="kv"><strong>RUT:</strong> ${opts.customerRut}</div>
          <div class="kv"><strong>Contrato:</strong> ${opts.contractNumber}</div>
        </div>
        <div class="section">
          <strong>Documentos pagados</strong>
          <table>
            <thead><tr><th>Documento</th><th>Vence</th><th class="right">Monto</th></tr></thead>
            <tbody>
              ${opts.rows.map((row) => `
                <tr>
                  <td>${row.label}</td>
                  <td>${new Date(row.dueDate).toLocaleDateString("es-CL")}</td>
                  <td class="right">$${row.amount.toLocaleString("es-CL")}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
        <div class="total">Total pagado: $${opts.total.toLocaleString("es-CL")}</div>
        <div class="footer">
          Pago registrado el ${new Date(opts.paidAt).toLocaleString("es-CL")}
          ${opts.reference ? `<br />Ref: ${opts.reference}` : ""}
        </div>
      </div>
    </body>
  </html>`;
}

export function buildCashClosureReceiptHtml(opts: {
  closureCode: string;
  businessDate: string;
  closedAt: string;
  closedByName?: string | null;
  notes?: string | null;
  totals: {
    payments: number;
    income: number;
    expense: number;
    net: number;
  };
  byPaymentMethod: Array<{
    code: string;
    name: string;
    count: number;
    amount: number;
  }>;
  byMovementType: Array<{
    type: string;
    count: number;
    amount: number;
  }>;
}): string {
  const logo = receiptLogo;
  return `<html>
    <head>
      <title>Arqueo ${opts.closureCode}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        html, body { width: 72mm; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; line-height: 1.35; }
        .ticket { width: 72mm; }
        .logo-wrap { text-align: center; margin-bottom: 8px; }
        .logo { max-width: 38mm; max-height: 18mm; object-fit: contain; }
        .title { text-align: center; font-size: 16px; font-weight: 700; margin: 0; }
        .muted { color: #4b5563; font-size: 10px; text-align: center; }
        .section { margin-top: 10px; }
        .kv { margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 6px; }
        th, td { border-bottom: 1px dashed #d1d5db; padding: 5px 0; font-size: 10px; vertical-align: top; }
        th { text-align: left; }
        .right { text-align: right; }
        .total { font-size: 13px; font-weight: 700; text-align: right; margin-top: 10px; }
        .footer { margin-top: 10px; text-align: center; font-size: 10px; color: #4b5563; }
      </style>
    </head>
    <body>
      <div class="ticket">
        <div class="logo-wrap"><img class="logo" src="${logo}" alt="APR" /></div>
        <h1 class="title">Arqueo de caja</h1>
        <div class="muted">${opts.closureCode}</div>
        <div class="section">
          <div class="kv"><strong>Fecha:</strong> ${new Date(opts.businessDate).toLocaleDateString("es-CL")}</div>
          <div class="kv"><strong>Cierre:</strong> ${new Date(opts.closedAt).toLocaleString("es-CL")}</div>
          <div class="kv"><strong>Usuario:</strong> ${opts.closedByName ?? "Sistema"}</div>
        </div>
        <div class="section">
          <strong>Pagos por forma de pago</strong>
          <table>
            <thead><tr><th>Metodo</th><th class="right">Cant.</th><th class="right">Monto</th></tr></thead>
            <tbody>
              ${opts.byPaymentMethod.map((row) => `
                <tr>
                  <td>${row.name}</td>
                  <td class="right">${row.count}</td>
                  <td class="right">$${row.amount.toLocaleString("es-CL")}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
        <div class="section">
          <strong>Movimientos de caja</strong>
          <table>
            <thead><tr><th>Tipo</th><th class="right">Cant.</th><th class="right">Monto</th></tr></thead>
            <tbody>
              ${opts.byMovementType.map((row) => `
                <tr>
                  <td>${row.type}</td>
                  <td class="right">${row.count}</td>
                  <td class="right">$${row.amount.toLocaleString("es-CL")}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
        <div class="section">
          <div class="kv"><strong>Total pagos:</strong> $${opts.totals.payments.toLocaleString("es-CL")}</div>
          <div class="kv"><strong>Ingresos caja:</strong> $${opts.totals.income.toLocaleString("es-CL")}</div>
          <div class="kv"><strong>Egresos caja:</strong> $${opts.totals.expense.toLocaleString("es-CL")}</div>
        </div>
        <div class="total">Neto arqueo: $${opts.totals.net.toLocaleString("es-CL")}</div>
        <div class="footer">
          ${opts.notes ? `Notas: ${opts.notes}<br />` : ""}
          Comprobante generado el ${new Date().toLocaleString("es-CL")}
        </div>
      </div>
    </body>
  </html>`;
}

export function buildPaymentVoidReceiptHtml(opts: {
  voidCode: string;
  receiptNumber: string;
  paidAtOriginal: string;
  voidedAt: string;
  voidedByName?: string | null;
  reason: string;
  customerName: string;
  customerRut: string;
  contractNumber: string;
  paymentMethodName?: string | null;
  rows: Array<{ label: string; dueDate: string | null; amount: number }>;
  total: number;
}): string {
  const logo = receiptLogo;
  return `<html>
    <head>
      <title>Anulacion ${opts.voidCode}</title>
      <style>
        @page { size: 80mm auto; margin: 4mm; }
        html, body { width: 72mm; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; color: #111827; font-size: 11px; line-height: 1.35; }
        .ticket { width: 72mm; }
        .logo-wrap { text-align: center; margin-bottom: 8px; }
        .logo { max-width: 38mm; max-height: 18mm; object-fit: contain; }
        .title { text-align: center; font-size: 16px; font-weight: 700; margin: 0; }
        .muted { color: #4b5563; font-size: 10px; text-align: center; }
        .section { margin-top: 10px; }
        .kv { margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 6px; }
        th, td { border-bottom: 1px dashed #d1d5db; padding: 5px 0; font-size: 10px; vertical-align: top; }
        th { text-align: left; }
        .right { text-align: right; }
        .total { font-size: 13px; font-weight: 700; text-align: right; margin-top: 10px; }
        .footer { margin-top: 10px; text-align: center; font-size: 10px; color: #4b5563; }
      </style>
    </head>
    <body>
      <div class="ticket">
        <div class="logo-wrap"><img class="logo" src="${logo}" alt="APR" /></div>
        <h1 class="title">Anulacion de pago</h1>
        <div class="muted">${opts.voidCode}</div>
        <div class="section">
          <div class="kv"><strong>Comprobante:</strong> ${opts.receiptNumber}</div>
          <div class="kv"><strong>Cliente:</strong> ${opts.customerName}</div>
          <div class="kv"><strong>RUT:</strong> ${opts.customerRut}</div>
          <div class="kv"><strong>Contrato:</strong> ${opts.contractNumber}</div>
          <div class="kv"><strong>Metodo:</strong> ${opts.paymentMethodName ?? "-"}</div>
        </div>
        <div class="section">
          <strong>Documentos liberados</strong>
          <table>
            <thead><tr><th>Documento</th><th>Vence</th><th class="right">Monto</th></tr></thead>
            <tbody>
              ${opts.rows.map((row) => `
                <tr>
                  <td>${row.label}</td>
                  <td>${row.dueDate ? new Date(row.dueDate).toLocaleDateString("es-CL") : "-"}</td>
                  <td class="right">$${row.amount.toLocaleString("es-CL")}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
        <div class="section">
          <div class="kv"><strong>Pago original:</strong> ${new Date(opts.paidAtOriginal).toLocaleString("es-CL")}</div>
          <div class="kv"><strong>Anulado:</strong> ${new Date(opts.voidedAt).toLocaleString("es-CL")}</div>
          <div class="kv"><strong>Usuario:</strong> ${opts.voidedByName ?? "Sistema"}</div>
          <div class="kv"><strong>Motivo:</strong> ${opts.reason}</div>
        </div>
        <div class="total">Monto anulado: $${opts.total.toLocaleString("es-CL")}</div>
        <div class="footer">Comprobante interno de anulacion</div>
      </div>
    </body>
  </html>`;
}

export function openPrintWindow(html: string): void {
  const popup = window.open("", "_blank", "width=360,height=700");
  if (!popup) return;
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  const triggerPrint = () => { popup.focus(); popup.print(); };
  if (popup.document.readyState === "complete") {
    setTimeout(triggerPrint, 250);
  } else {
    popup.onload = () => setTimeout(triggerPrint, 250);
  }
}

export function downloadReceiptHtml(html: string, receiptNumber: string): void {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `comprobante-${receiptNumber}.html`;
  a.click();
  URL.revokeObjectURL(url);
}
